import { EventEmitter, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { from } from 'rxjs';
import { JoinResolverData } from 'src/app/pages/join/join.resolver';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AuthUser, OAuthCompany, SsoProviderType, User } from 'src/models';
import { ApiService } from '../api.service';

export interface TokenResult {
  token: string;
  expires: string;
  createdAt: string;
}

export interface SsoOtpResponse {
  SSO_OTP: boolean;
}

export type LoginResult = (AuthUser & { token?: string }) | { status: 'totpNeeded' };

export type EditProfileProps = Partial<User> & { password?: string };

export type TokenScope = 'profile' | 'onboarding';

export type CredentialAuthResponse = {
  isNewUser?: boolean;
  emailVerificationRequired?: boolean;
  data?: {
    createdAt?: string;
    expiresAt?: string;
    token?: string;
  };
  error?: string;
  message?: string;
};

@Injectable()
export abstract class AuthService {
  readonly onLogout = new EventEmitter<void>();

  private cachedToken: string | null = null;

  async getCachedScopedToken(scope: TokenScope): Promise<string | null> {
    if (!this.store.selectSnapshot(AuthState.authorized)) return '';

    this.cachedToken ??= await this.getScopedToken(scope);
    return this.cachedToken;
  }

  get scopedTokenStream() {
    return from(this.getCachedScopedToken('profile'));
  }

  constructor(protected api: ApiService, protected store: Store) {
    // Clear cached token on logout
    this.onLogout.subscribe(() => this.cachedToken = null);

    // Clear cached token every minute
    setInterval(() => this.cachedToken = null, 1000 * 60);
  }

  abstract get version(): 'v1' | 'v2';

  abstract refreshToken(): Promise<unknown>;

  abstract login(email: string, password: string, totpCode?: string): Promise<LoginResult>;

  abstract logout(): Promise<unknown>;

  abstract auth(): Promise<AuthUser>;

  abstract getScopedToken(scope: TokenScope): Promise<string>;

  abstract resetPassword(token: string, password: string): Promise<unknown>;

  abstract forgotPassword(email: string): Promise<unknown>;

  abstract getTokenHeader(token: string): Record<string, string>;

  abstract oauthLogin(provider: SsoProviderType, token: string): Promise<{ token: string }>;

  abstract findCompaniesByOTP(email: string, issuer: string, otp: string): Promise<OAuthCompany[]>;

  abstract isSsoOtpEnabled(email: string): Promise<SsoOtpResponse>;

  abstract sendOtpRequest(email: string): Promise<unknown>;

  abstract register(email: string, name: string, password: string, params?: any): Promise<TokenResult | undefined>;

  abstract editProfile(properties: EditProfileProps): Promise<TokenResult>;

  abstract validateMagicLinkToken(token: string): Promise<JoinResolverData>;

  abstract oauthGoogleLogin(token: string): Promise<CredentialAuthResponse>;

  private b64DecodeUnicode(str: string): string {
    return decodeURIComponent(Array.prototype.map.call(atob(str), c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
  }

  protected parseJwt(token: string): any {
    const decodedString = this.b64DecodeUnicode(
      token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'),
    );
    return JSON.parse(decodedString);
  }
}
